import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, ChevronRight, ClipboardList, Heart, Loader2, Mail, Shirt } from 'lucide-react';
import { toast } from 'sonner';
import { useRegister } from '@/hooks/use-auth';
import { isPlausiblePhone, toE164 } from '@/lib/phone';
import { AuthLayout, PhoneField, SecretField } from './AuthLayout';

/**
 * Un club ne s'inscrit pas ici : son compte est créé par l'administrateur de la
 * plateforme, qui provisionne en même temps la fiche du club.
 */
type UserType = 'player' | 'coach';

const PROFILES = {
  player: { label: 'Joueur', icon: Shirt },
  coach: { label: 'Entraîneur', icon: ClipboardList },
} as const;

/** Quatre crans, dans la palette : le rouge alerte, le vert rassure. */
function passwordStrength(password: string): { level: number; text: string; color: string } {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password) && /[^a-zA-Z0-9]/.test(password)) score++;

  const levels = [
    { text: 'Trop court', color: 'bg-danger' },
    { text: 'Faible', color: 'bg-danger' },
    { text: 'Moyen', color: 'bg-accent-500' },
    { text: 'Bon', color: 'bg-primary-400' },
    { text: 'Excellent', color: 'bg-primary-600' },
  ];
  return { level: score, ...levels[score] };
}

function ProfileCard({
  icon: Icon,
  title,
  description,
  onClick,
}: {
  icon: typeof Shirt;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 hover:bg-primary-50"
    >
      <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-primary-50 text-primary-700 group-hover:bg-white">
        <Icon className="h-5 w-5" />
      </span>
      <span className="flex-1">
        <span className="block font-semibold text-gray-900">{title}</span>
        <span className="block text-sm text-gray-500">{description}</span>
      </span>
      <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-primary-600" />
    </button>
  );
}

export function RegisterForm() {
  const navigate = useNavigate();
  const [userType, setUserType] = useState<UserType | null>(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', password: '' });
  const [acceptTerms, setAcceptTerms] = useState(false);

  const { mutate: register, isPending } = useRegister();

  const set = (field: keyof typeof form) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userType || !acceptTerms) return;

    if (!isPlausiblePhone(form.phone)) {
      toast.error('Entrez votre numéro de téléphone complet.');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    // Le serveur n'accepte que la forme +221771234567 : c'est aussi celle que
    // la connexion par téléphone retrouve.
    register({ ...form, phone: toE164(form.phone), user_type: userType });
  };

  const footer = (
    <p className="text-center text-sm text-gray-600">
      Déjà inscrit ?{' '}
      <Link to="/login" className="font-semibold text-primary-700 hover:underline">
        Se connecter
      </Link>
    </p>
  );

  /* Étape 1 : qui êtes-vous ? --------------------------------------------- */
  if (!userType) {
    return (
      <AuthLayout
        title="Créer un compte"
        subtitle="Choisissez le profil qui vous correspond."
        footer={footer}
      >
        <div className="space-y-3">
          <ProfileCard
            icon={Heart}
            title="Supporter"
            description="Je suis mon club. Un numéro et un code, sans e-mail."
            onClick={() => navigate('/supporter?mode=register')}
          />
          <ProfileCard
            icon={Shirt}
            title="Joueur"
            description="Je cherche un club ou des opportunités."
            onClick={() => setUserType('player')}
          />
          <ProfileCard
            icon={ClipboardList}
            title="Entraîneur"
            description="Je gère des équipes et des joueurs."
            onClick={() => setUserType('coach')}
          />

          {/* Les clubs ne s'inscrivent pas seuls : l'entrée reste visible pour
              dire où s'adresser, plutôt que de manquer sans explication. */}
          <div className="flex items-start gap-4 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4">
            <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-white text-gray-400">
              <Building2 className="h-5 w-5" />
            </span>
            <p className="text-sm text-gray-600">
              <span className="block font-semibold text-gray-700">Vous êtes un club ?</span>
              Les comptes club sont ouverts par l'administrateur de la plateforme. Contactez-le
              pour créer l'espace de votre club.
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  /* Étape 2 : le formulaire ------------------------------------------------ */
  const profile = PROFILES[userType];
  const strength = passwordStrength(form.password);

  return (
    <AuthLayout
      title={`Compte ${profile.label.toLowerCase()}`}
      subtitle="Quelques informations et c'est parti."
      footer={footer}
    >
      <button
        type="button"
        onClick={() => setUserType(null)}
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Changer de profil
      </button>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="first_name" className="label">
              Prénom
            </label>
            <input
              id="first_name"
              autoComplete="given-name"
              autoFocus
              required
              minLength={2}
              value={form.first_name}
              onChange={(e) => set('first_name')(e.target.value)}
              placeholder="Sadio"
              className="input"
            />
          </div>
          <div>
            <label htmlFor="last_name" className="label">
              Nom
            </label>
            <input
              id="last_name"
              autoComplete="family-name"
              required
              minLength={2}
              value={form.last_name}
              onChange={(e) => set('last_name')(e.target.value)}
              placeholder="Mané"
              className="input"
            />
          </div>
        </div>

        <PhoneField
          id="phone"
          label="Numéro de téléphone"
          value={form.phone}
          onChange={set('phone')}
          hint="Il vous servira à vous connecter."
        />

        <div>
          <label htmlFor="email" className="label">
            Adresse e-mail
          </label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(e) => set('email')(e.target.value)}
              placeholder="exemple@email.com"
              className="input pl-9"
            />
          </div>
        </div>

        <div>
          <SecretField
            id="password"
            label="Mot de passe"
            value={form.password}
            onChange={set('password')}
            autoComplete="new-password"
            hint={!form.password ? '8 caractères minimum. Ce sera aussi votre code de connexion.' : undefined}
          />
          {form.password && (
            <div className="mt-2">
              <div className="flex gap-1">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full ${i < strength.level ? strength.color : 'bg-gray-200'}`}
                  />
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">{strength.text}</p>
            </div>
          )}
        </div>

        <label className="flex items-start gap-3 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span>
            J'accepte les{' '}
            <a href="#" className="font-medium text-primary-700 hover:underline">
              conditions d'utilisation
            </a>{' '}
            et la{' '}
            <a href="#" className="font-medium text-primary-700 hover:underline">
              politique de confidentialité
            </a>
            .
          </span>
        </label>

        <button
          type="submit"
          disabled={isPending || !acceptTerms}
          className="btn-primary w-full py-3 text-base"
        >
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isPending ? 'Création du compte…' : 'Créer mon compte'}
        </button>
      </form>
    </AuthLayout>
  );
}
